import React from 'react';

import { Control, Controller, FieldErrors, UseFormRegister } from 'react-hook-form';
import * as yup from 'yup';

import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, Stack, TextField } from '@mui/material';

import DateTimeWidget from '~/components/Common/Elements/DateTimeWidget';
import { invoiceCreateSchema } from '~/components/InvoiceTools/schema/invoice.schema';

type InvoiceCreate = yup.InferType<typeof invoiceCreateSchema>;

interface PaymentFieldProps {
  index: number;
  register: UseFormRegister<InvoiceCreate>;
  errors: FieldErrors<InvoiceCreate>;
  control: Control<InvoiceCreate>;
  onRemove: () => void;
  canRemove: boolean;
  disabled?: boolean;
}

export default function PaymentField({
  index,
  register,
  errors,
  control,
  onRemove,
  canRemove,
  disabled,
}: PaymentFieldProps) {
  const paymentErrors = errors.payments?.[index];

  return (
    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1, mb: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ typography: 'subtitle2', color: 'text.secondary' }}>Payment {index + 1}</Box>
          {canRemove && (
            <IconButton color="error" onClick={onRemove} disabled={disabled} size="small">
              <DeleteIcon />
            </IconButton>
          )}
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            {...register(`payments.${index}.description`)}
            label="Description"
            fullWidth
            required
            disabled={disabled}
            error={!!paymentErrors?.description}
            helperText={paymentErrors?.description?.message}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <TextField
            {...register(`payments.${index}.amount`, { valueAsNumber: true })}
            label="Amount"
            type="number"
            fullWidth
            required
            disabled={disabled}
            error={!!paymentErrors?.amount}
            helperText={paymentErrors?.amount?.message}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: '0.01' } }}
          />

          <Controller
            name={`payments.${index}.date`}
            control={control}
            render={({ field }) => (
              <DateTimeWidget
                value={field.value instanceof Date ? field.value.toISOString() : String(field.value)}
                onChange={(value) => field.onChange(new Date(value))}
                label="Payment Date"
                disabled={disabled}
                required
              />
            )}
          />
        </Stack>
      </Stack>
    </Box>
  );
}
